import React, { useState, useMemo } from 'react';
import { Map, MapPin, Database, ArrowRight } from 'lucide-react';
import { Card } from '../ui';
import { Button } from '../ui';
import { Textarea } from '../ui';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const REGIONS: Record<string, { name: string; color: string }> = {
  '0': { name: 'Northeast', color: '#3b82f6' },
  '1': { name: 'Mid-Atlantic', color: '#8b5cf6' },
  '2': { name: 'South', color: '#ec4899' },
  '3': { name: 'Southeast', color: '#f43f5e' },
  '4': { name: 'Midwest', color: '#f97316' },
  '5': { name: 'Northern Plains', color: '#eab308' },
  '6': { name: 'Central Plains', color: '#84cc16' },
  '7': { name: 'South Central', color: '#10b981' },
  '8': { name: 'Mountain', color: '#06b6d4' },
  '9': { name: 'West Coast', color: '#0ea5e9' }
};

const SAMPLE_DATA = Array.from({ length: 500 }, () => {
  // Generate random 5 digit zip codes, heavily skewed to 1, 9, and 3
  const r = Math.random();
  let firstDigit = '0';
  if (r > 0.8) firstDigit = '9';
  else if (r > 0.5) firstDigit = '1';
  else if (r > 0.3) firstDigit = '3';
  else firstDigit = Math.floor(Math.random() * 10).toString();
  
  return firstDigit + Math.floor(1000 + Math.random() * 9000).toString();
}).join(', ');

export function AlumniDensityMapper() {
  const [inputText, setInputText] = useState<string>('');

  const mappedData = useMemo(() => {
    if (!inputText.trim()) return [];

    // Extract all 5-digit sequences
    const matches = inputText.match(/\b\d{5}\b/g) || [];
    if (matches.length === 0) return [];

    const counts: Record<string, number> = {};
    matches.forEach(zip => {
      const firstDigit = zip.charAt(0);
      counts[firstDigit] = (counts[firstDigit] || 0) + 1;
    });

    const results = Object.keys(counts).map(digit => ({
      digit,
      region: REGIONS[digit]?.name || 'Unknown',
      color: REGIONS[digit]?.color || '#cbd5e1',
      count: counts[digit],
      percentage: ((counts[digit] / matches.length) * 100).toFixed(1)
    }));

    return results.sort((a, b) => b.count - a.count);
  }, [inputText]);

  return (
    <div className="max-w-6xl mx-auto space-y-8 p-4">
      
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-black flex items-center mb-2">
            <Map className="w-6 h-6 mr-3 text-blue-200" />
            Chapter Expansion Density Mapper
          </h2>
          <p className="text-blue-100 max-w-xl">
            Paste a raw list of zip codes from your CRM or alumni database. We'll automatically extract valid codes and cluster them into macro-regions to identify the best locations for your next regional chapter.
          </p>
        </div>
        <Button 
          onClick={() => setInputText(SAMPLE_DATA)}
          variant="secondary"
          className="bg-white text-blue-700 hover:bg-blue-50 font-bold shrink-0"
        >
          <Database className="w-4 h-4 mr-2" />
          Load Sample Dataset
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        
        {/* Input */}
        <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-lg bg-white dark:bg-slate-900 flex flex-col">
          <label className="text-sm font-semibold text-slate-500 uppercase flex items-center mb-4">
            <MapPin className="w-4 h-4 mr-2 text-blue-500" /> Paste Zip Codes
          </label>
          <Textarea 
            placeholder="e.g. 10001, 90210, 60601...&#10;Raw text or CSV formats are supported."
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            className="flex-1 min-h-[300px] resize-none font-mono text-sm bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800"
          />
          <div className="mt-4 flex justify-between items-center text-sm text-slate-500">
            <span>Extracts 5-digit patterns automatically.</span>
            <Button variant="ghost" size="sm" onClick={() => setInputText('')}>Clear</Button>
          </div>
        </Card>

        {/* Output */}
        <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-lg bg-white dark:bg-slate-900">
          <h3 className="text-lg font-bold mb-6 flex items-center">
            <ArrowRight className="w-5 h-5 mr-2 text-blue-500" /> Clustering Results
          </h3>
          
          {mappedData.length > 0 ? (
            <div className="space-y-6">
              <div className="h-[200px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={mappedData} layout="vertical" margin={{ top: 0, right: 0, bottom: 0, left: 40 }}>
                    <XAxis type="number" hide />
                    <YAxis dataKey="region" type="category" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                    <Tooltip cursor={{ fill: 'transparent' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}/>
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      {mappedData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="border-t border-slate-100 dark:border-slate-800 pt-6">
                <h4 className="font-semibold text-sm mb-3">Top Recommendations</h4>
                <div className="space-y-3">
                  {mappedData.slice(0, 3).map((item, idx) => (
                    <div key={item.region} className="flex justify-between items-center p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                      <div className="flex items-center">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-xs mr-3" style={{ backgroundColor: item.color }}>
                          #{idx + 1}
                        </div>
                        <span className="font-semibold">{item.region}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">{item.count.toLocaleString()}</div>
                        <div className="text-xs text-slate-500">{item.percentage}% of total</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="h-[300px] flex flex-col items-center justify-center text-slate-400 dark:text-slate-500 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl">
              <Map className="w-12 h-12 mb-4 opacity-20" />
              <p>Paste zip codes to see cluster results.</p>
            </div>
          )}
        </Card>

      </div>
    </div>
  );
}
